/* eslint-disable @typescript-eslint/no-use-before-define */
import { Input } from '@euler/components/form/Input';
import { MaybeText } from '@euler/components/MaybeText';
import { ModalPanDownGestureProvider } from '@euler/components/ModalScrollView';
import { ModalWrapper, ModalWrapperRef } from '@euler/components/ModalWrapper';
import { TableView } from '@euler/components/TableView';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useFormController } from '@euler/lib/form/controller';
import {
  FC,
  memo,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';

type FormData = {
  brand: string;
  model: string;
};

export const AddVehicleInfoScreen = wrapNavigatorScreen(
  ({
    brand,
    onConfirm,
  }: {
    brand?: string;
    onConfirm?: (brand: string, model: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();

    const {
      order: { placeholderColor },
      label: { color: btnTextColor, destructiveColor },
      components: {
        table: { borderColor },
      },
    } = useTheme();

    const { form, validateAll } = useFormController<FormData>({
      brand: { required: true, defaultValue: brand ?? '' },
      model: { required: true, defaultValue: '' },
    });

    const brandInputRef = useRef<TextInput>(null);
    const modelInputRef = useRef<TextInput>(null);

    const onBrandPress = useCallback(() => {
      brandInputRef.current?.focus();
    }, []);

    const onModelPress = useCallback(() => {
      modelInputRef.current?.focus();
    }, []);

    const modalRef = useRef<ModalWrapperRef>(null);

    const onConfirmPress = useCallback(async () => {
      const isValid = await validateAll();
      if (isValid) {
        modalRef.current?.dismiss(() => {
          onConfirm?.(form.brand.value.trim(), form.model.value.trim());
        });
      }
    }, [form.brand.value, form.model.value, onConfirm, validateAll]);

    const onCancelPress = useCallback(() => {
      modalRef.current?.dismiss();
    }, []);

    return (
      <ModalWrapper dismissible={true} height={rpx(230)} ref={modalRef}>
        <ModalPanDownGestureProvider>
          <TableView>
            <TableView.Section style={styles.section}>
              <TableView.Item
                style={styles.row}
                separatorStyle={styles.rowSeparator}
                onPress={onBrandPress}
              >
                <Cell label="车辆品牌">
                  <Input
                    inputRef={brandInputRef}
                    placeholderTextColor={placeholderColor}
                    value={form.brand.value}
                    placeholder="请输入车辆品牌信息"
                    errorMsg={form.brand.error}
                    style={styles.formInput}
                    onChangeText={form.brand.update}
                    onBlur={form.brand.onBlur}
                  />
                </Cell>
              </TableView.Item>
              <TableView.Item
                style={styles.row}
                separatorStyle={styles.rowSeparator}
                onPress={onModelPress}
              >
                <Cell label="车辆名称">
                  <Input
                    inputRef={modelInputRef}
                    placeholderTextColor={placeholderColor}
                    value={form.model.value}
                    placeholder="请输入车辆名称信息"
                    style={styles.formInput}
                    errorMsg={form.model.error}
                    onChangeText={form.model.update}
                    onBlur={form.model.onBlur}
                  />
                </Cell>
              </TableView.Item>
            </TableView.Section>
          </TableView>
          <View
            css={`
              position: absolute;
              left: 0;
              right: 0;
              bottom: 0;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
              border-top-color: ${borderColor};
              border-top-width: 1px;
            `}
          >
            <TouchableOpacity
              css={`
                border-right-color: ${borderColor};
                border-right-width: 1px;
                align-items: center;
                justify-content: center;
                flex: 1;
                height: ${rpx(44)}px;
              `}
              onPress={onCancelPress}
            >
              <Text
                css={`
                  color: ${destructiveColor};
                  font-family: ${FontFamily.NotoSans.Regular};
                  font-size: ${rpx(16)}px;
                  line-height: ${rpx(18)}px;
                `}
              >
                取消
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              css={`
                align-items: center;
                justify-content: center;
                flex: 1;
                height: ${rpx(44)}px;
              `}
              onPress={onConfirmPress}
            >
              <Text
                css={`
                  color: ${btnTextColor};
                  font-family: ${FontFamily.NotoSans.Regular};
                  font-size: ${rpx(16)}px;
                  line-height: ${rpx(18)}px;
                `}
              >
                确定
              </Text>
            </TouchableOpacity>
          </View>
        </ModalPanDownGestureProvider>
      </ModalWrapper>
    );
  },
  {
    presentation: 'transparentModal',
    cardOverlayEnabled: true,
    animationEnabled: false,
    detachPreviousScreen: false,
    cardStyle: {
      backgroundColor: 'transparent',
    },
    headerShown: false,
  },
);

const Cell: FC<
  PropsWithChildren<{ label: ReactNode; style?: StyleProp<ViewStyle> }>
> = memo(({ label, children, style }) => {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  return (
    <View
      css={`
        padding: 0;
        margin: 0;
      `}
      style={style}
    >
      <MaybeText
        text={label}
        css={`
          font-family: ${FontFamily.NotoSans.Regular};
          font-size: ${rpx(14)}px;
          line-height: ${rpx(16)}px;
          color: ${theme.inspection.customIssue.labelColor};
          margin-bottom: ${rpx(5)}px;
        `}
      />
      {children}
    </View>
  );
});

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        formInput: {
          borderWidth: 0,
          backgroundColor: '#fff',
          paddingLeft: rpx(8),
          paddingRight: rpx(8),
          paddingTop: 0,
          paddingBottom: 0,
          fontSize: rpx(16),
        },
        section: {
          backgroundColor: '#fff',
          marginTop: rpx(15),
          borderRadius: rpx(8),
        },
        row: {
          padding: rpx(15),
          height: rpx(80),
        },
        rowSeparator: {
          backgroundColor: '#f2f2f2',
        },
      }),
    [rpx],
  );
};
